import React, { useState, useEffect, useCallback } from 'react';
import SocialActions from '../../components/SocialActions/SocialActions';
import ContentEditorModal from '../../components/ContentEditorModal/ContentEditorModal';
import { useAuth } from '../../contexts/AuthContext';

const Hibuat = () => {
  const { user } = useAuth();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('amharic');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSections, setFilteredSections] = useState([]);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isNewContent, setIsNewContent] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const apiURL = process.env.REACT_APP_API_URL || '/api';

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiURL}/content?type=hibuat`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const mapped = json.data.map(item => ({
            id: item.itemId || item._id,
            _id: item._id,
            title: item.title,
            titleEnglish: item.category || item.metadata?.titleEnglish || '',
            content: item.content || { amharic: '', geez: '' },
            raw: item
          }));
          setSections(mapped);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('API error for Hibuat:', err.message);
    }
    setSections([]);
    setLoading(false);
  }, [apiURL]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSections(sections);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = sections.filter(section =>
        section.title?.toLowerCase().includes(term) ||
        section.titleEnglish?.toLowerCase().includes(term) ||
        section.content?.amharic?.toLowerCase().includes(term) ||
        section.content?.geez?.toLowerCase().includes(term)
      );
      setFilteredSections(filtered);
    }
  }, [searchTerm, sections]);

  const handleSectionClick = (section) => {
    setSelectedSection(selectedSection?.id === section.id ? null : section);
  };

  const highlightText = (text) => {
    if (!searchTerm.trim() || !text) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  };

  const formatText = (text) => {
    if (!text) return '';
    const clean = typeof text === 'string' ? text.replace(/\\n/g, '\n') : text;
    return clean.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>');
  };

  const handleOpenEdit = (section, e) => {
    e.stopPropagation();
    setEditingItem({
      _id: section._id,
      contentType: 'hibuat',
      title: section.title,
      itemId: section.id,
      category: section.titleEnglish,
      content: section.content
    });
    setIsNewContent(false);
    setEditorOpen(true);
  };

  const handleOpenInsert = () => {
    const nextId = sections.length > 0 ? Math.max(...sections.map(s => typeof s.id === 'number' ? s.id : 0)) + 1 : 1;
    setEditingItem({
      contentType: 'hibuat',
      title: '',
      category: 'Section ' + nextId,
      itemId: nextId,
      content: {
        amharic: '',
        geez: ''
      }
    });
    setIsNewContent(true);
    setEditorOpen(true);
  };

  const canEditContent = user?.canEdit || user?.canUpdate || user?.isAdmin;
  const canInsertContent = user?.canInsert || user?.isAdmin;

  const handleEditorSaved = () => {
    loadSections();
  };

  return (
    <div className="page-container hibuat-theme">
      {/* Enhanced Page Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="page-main-title">
              📜 መጽሐፈ ኅቡዓት
            </h1>
            <div className="page-subtitle">
              Hidden Mysteries - የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተ ክርስቲያን ሚስጥራዊ ትምህርቶች (ከዳታቤዝ)
            </div>
            <div className="page-description">
              <p className="page-description-main">
                የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተ ክርስቲያን ሚስጥራዊ ትምህርቶች እና ጸሎቶች
              </p>
              <p className="page-description-sub">
                Sacred teachings and hidden mysteries of the Ethiopian Orthodox Tewahedo Church
              </p>
            </div>
          </div>
          {canInsertContent && (
            <button
              onClick={handleOpenInsert}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-2 self-start sm:self-auto cursor-pointer"
            >
              <span>➕</span>
              <span>አዲስ ኅቡዕ ክፍል አክል</span>
            </button>
          )}
        </div>
      </div>

      {/* Controls Section */}
      <div className="controls-container">
        <div className="controls-grid">
          <div className="control-group">
            <label htmlFor="hibuat-search" className="control-label">
              🔍 ፈልግ (Search)
            </label>
            <input
              id="hibuat-search"
              type="text"
              placeholder="በኅቡዓት ትምህርቶች ውስጥ ፈልግ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="control-input"
            />
          </div>

          <div className="control-group">
            <label className="control-label">🌐 ቋንቋ (Language):</label>
            <div className="language-buttons flex gap-2">
              <button
                onClick={() => setSelectedLanguage('amharic')}
                className={`language-btn px-3 py-2 rounded-lg border transition-all duration-200 text-sm font-medium ${selectedLanguage === 'amharic' ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-transparent shadow-md' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
              >
                አማርኛ
              </button>
              <button
                onClick={() => setSelectedLanguage('geez')}
                className={`language-btn px-3 py-2 rounded-lg border transition-all duration-200 text-sm font-medium ${selectedLanguage === 'geez' ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-transparent shadow-md' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
              >
                ግዕዝ
              </button>
              <button
                onClick={() => setSelectedLanguage('both')}
                className={`language-btn px-3 py-2 rounded-lg border transition-all duration-200 text-sm font-medium ${selectedLanguage === 'both' ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-transparent shadow-md' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
              >
                ሁለቱም
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="hibuat-content">
        {/* Sections List */}
        <div className="content-container">
          <div className="content-header">
            <div className="content-icon">📚</div>
            <h2 className="content-title">
              የኅቡዓት ክፍሎች ({filteredSections.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">በመጫን ላይ...</div>
          ) : filteredSections.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <p className="no-results-title">ምንም ውጤት አልተገኘም</p>
              <p className="no-results-subtitle">ሌላ ቃል ይሞክሩ</p>
            </div>
          ) : (
            <div className="card-grid">
              {filteredSections.map((section) => (
                <div
                  key={section.id}
                  className={`card-item ${selectedSection?.id === section.id ? 'active' : ''}`}
                  onClick={() => handleSectionClick(section)}
                >
                  <div className="card-header flex justify-between items-center">
                    <h3 className="card-title">
                      <span className="card-number">{section.id}</span>
                      <span 
                        dangerouslySetInnerHTML={{
                          __html: highlightText(section.title)
                        }}
                      />
                    </h3>
                    <div className="flex items-center gap-2">
                      {canEditContent && (
                        <button
                          onClick={(e) => handleOpenEdit(section, e)}
                          className="bg-amber-500 hover:bg-amber-600 text-white text-xs px-2.5 py-1 rounded shadow-sm transition-all cursor-pointer"
                        >
                          ✏️ አርትዕ
                        </button>
                      )}
                      <span className="card-toggle text-xl">
                        {selectedSection?.id === section.id ? '📖' : '📕'}
                      </span>
                    </div>
                  </div>
                  
                  {section.titleEnglish && <p className="card-subtitle">{section.titleEnglish}</p>}
                  
                  {selectedSection?.id === section.id && (
                    <div className="card-content">
                      {(selectedLanguage === 'amharic' || selectedLanguage === 'both') && section.content?.amharic && (
                        <div className="content-block amharic-content">
                          <div className="content-header">
                            <span className="language-indicator amharic">አማርኛ</span>
                          </div>
                          <div className="content-text">
                            <p 
                              dangerouslySetInnerHTML={{
                                __html: highlightText(formatText(section.content.amharic))
                              }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {(selectedLanguage === 'geez' || selectedLanguage === 'both') && section.content?.geez && (
                        <div className="content-block geez-content">
                          <div className="content-header">
                            <span className="language-indicator geez">ግዕዝ</span>
                          </div>
                          <div className="content-text">
                            <p 
                              dangerouslySetInnerHTML={{
                                __html: highlightText(formatText(section.content.geez))
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Social Actions */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <SocialActions
                          contentId={`hibuat_${section.id}`}
                          contentType="hibuat"
                          title={section.title}
                          content={selectedLanguage === 'geez' ? section.content?.geez : section.content?.amharic}
                          showBookmark={true}
                          showShare={true}
                          showLike={true}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions Section */}
        <div className="content-container">
          <div className="content-header">
            <div className="content-icon">📖</div>
            <h2 className="content-title">
              የኅቡዓት ትምህርት መመሪያ
            </h2>
          </div>
          
          <div className="instructions-grid">
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">🔍</span>
                <h3 className="instruction-title">እንዴት እንደምንጠቀም</h3>
              </div>
              <p className="instruction-description">
                ከላይ ባለው ፍለጋ ሳጥን ውስጥ የሚፈልጉትን ቃል ወይም ሐረግ ያስገቡ። በአማርኛ ወይም በግዕዝ መፈለግ ይችላሉ።
              </p>
            </div>

            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">🌐</span>
                <h3 className="instruction-title">ቋንቋ መምረጥ</h3>
              </div>
              <p className="instruction-description">
                የሚፈልጉትን ቋንቋ ይምረጡ፦ አማርኛ፣ ግዕዝ ወይም ሁለቱንም በአንድ ላይ ማየት ይችላሉ።
              </p>
            </div>

            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">📚</span>
                <h3 className="instruction-title">ክፍሎችን ማንበብ</h3>
              </div>
              <p className="instruction-description">
                በእያንዳንዱ ክፍል ላይ ጠቅ በማድረግ ሙሉውን ይዘት ማንበብ ይችላሉ። እንደገና ጠቅ ማድረግ ይዘቱን ይደብቃል።
              </p>
            </div>

            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">⭐</span>
                <h3 className="instruction-title">ልዩ ማስታወሻ</h3>
              </div>
              <p className="instruction-description">
                የኅቡዓት ትምህርቶች ጥልቅ መንፈሳዊ ትምህርቶች ናቸው። በጸሎት እና በትኩረት ያንብቡ።
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Editor Modal */}
      {editorOpen && (
        <ContentEditorModal
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          onSaved={handleEditorSaved}
          initialData={editingItem}
          isNew={isNewContent}
          contentType="hibuat"
        />
      )}
    </div>
  );
};

export default Hibuat;
